import React, { useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

import CasesAPI from 'api/CasesAPI';

export interface ITariffChangeLine {
  label: string;
  code: string | null;
  from: string;
  to: string;
}

/**
 * A scheduled tariff check's unreviewed change on a Tariff ticket, written by
 * the biteservices product service (tariff-ticket-monitor.service.ts) as
 * `tariffMonitor.pendingChange`: everything that moved since the user last
 * reviewed the ticket.
 */
export interface ITariffPendingChange {
  id: string;
  detectedAt: string;
  asOf: string;
  heading: string;
  previousValue: string;
  currentValue: string;
  message: string;
  changes?: ITariffChangeLine[];
}

interface ITariffChangeBannerProps {
  caseId: string;
  change: ITariffPendingChange;
  onReviewed: () => void;
}

const formatDay = (value: string): string => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '';
};

const TariffChangeBanner: React.FC<ITariffChangeBannerProps> = ({
  caseId,
  change,
  onReviewed,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const changes = Array.isArray(change.changes) ? change.changes : [];

  const markReviewed = async () => {
    setSaving(true);
    const resp: any = await CasesAPI.reviewTariffChange(caseId, change.id);
    setSaving(false);
    if (resp?.success) {
      enqueueSnackbar('Tariff update marked as reviewed', {
        variant: 'success',
      });
      onReviewed();
    } else if (resp?.status === 409) {
      // Already reviewed elsewhere, or a newer change replaced this one.
      onReviewed();
    } else {
      enqueueSnackbar('Could not mark the tariff update as reviewed', {
        variant: 'error',
      });
    }
  };

  return (
    <div className='tariff-change-banner' role='status'>
      <div className='tariff-change-banner-head'>
        <NotificationsActiveOutlinedIcon
          className='tariff-change-banner-icon'
          fontSize='small'
        />
        <div className='tariff-change-banner-heading'>
          <div className='tariff-change-banner-title'>
            Tariff update detected
          </div>
          <div className='tariff-change-banner-meta'>
            Found by the scheduled check on {formatDay(change.detectedAt)}
            {change.asOf ? ` · priced as of ${formatDay(change.asOf)}` : ''}
          </div>
        </div>
        <button
          type='button'
          className='btn-primary tariff-change-banner-action'
          onClick={markReviewed}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Mark as reviewed'}
        </button>
      </div>
      <div className='tariff-change-banner-headline'>
        {change.heading}: <span>{change.previousValue}</span> →{' '}
        <strong>{change.currentValue}</strong>
      </div>
      {changes.length > 0 && (
        <ul className='tariff-change-banner-list'>
          {changes.map((line, index) => (
            <li key={`${line.code ?? line.label}-${index}`}>
              <span className='tariff-change-banner-label'>
                {line.label}
                {line.code ? ` (${line.code})` : ''}
              </span>
              : {line.from} → <strong>{line.to}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TariffChangeBanner;
